import type { MediaKind, Widget, WidgetType } from '../contract.js';

/**
 * The widget grid engine: pure functions ported from the locked prototype (04 §3–§7, §15).
 * 7 columns × 6 rows, 68 px cells, 14 px gaps.
 */

export const COLS = 7;
export const ROWS = 6;
export const STEP = 82;
export const GAP = 14;

/** Pixel size of a span of `n` cells: 68, 150, 232, … */
export const span = (n: number): number => n * STEP - GAP;

export interface Rect { readonly c: number; readonly r: number; readonly w: number; readonly h: number }
export interface Cell { readonly c: number; readonly r: number }

/** A widget without placement, as passed to `place` / `addWidget`. */
export type WidgetSpec = Pick<Widget, 't'> & Partial<Widget>;

/** `wd`: a widget with default framing. */
export function wd(t: WidgetType, c: number, r: number, w: number, h: number, extra: Partial<Widget> = {}): Omit<Widget, 'id'> & { id?: string } {
  return { t, c, r, w, h, px: 0.5, py: 0.5, z: 1, ...extra };
}
/** `withIds`: ids `{prefix}_{index}`, kept when the item already has one. */
export function withIds(items: readonly (Omit<Widget, 'id'> & { id?: string })[], prefix: string): Widget[] {
  return items.map((item, index) => ({ ...item, id: item.id ?? `${prefix}_${index}` }));
}

const KIND_ASPECT: Readonly<Record<MediaKind, number>> = { landscape: 16 / 9, portrait: 3 / 4, square: 1, video: 16 / 9 };
/** `arOf`: the real aspect ratio when known, else the kind's fallback, else 1.5. */
export function arOf(item: Pick<Widget, 'ar' | 'kind'>): number {
  return item.ar || (item.kind ? KIND_ASPECT[item.kind] : undefined) || 1.5;
}
/** Zoom range of photos and videos. */
export const zRange = (): readonly [number, number] => [1, 3];
export const minW = (t: WidgetType): number => (t === 'song' ? 2 : 1);

/** `defSize`: the size a new widget asks for, in cells. */
export function defSize(item: WidgetSpec): readonly [number, number] {
  if (item.t === 'text') return [3, 2];
  if (item.t === 'song') return [3, 1];
  const a = arOf(item);
  return a > 1.2 ? [3, 2] : a < 0.85 ? [2, 3] : [2, 2];
}

/** `fits`: inside the grid and not over any other widget (`id` itself is ignored). */
export function fits(items: readonly (Rect & { readonly id?: string })[], id: string | null, c: number, r: number, w: number, h: number): boolean {
  if (c < 0 || r < 0 || c + w > COLS || r + h > ROWS) return false;
  for (const other of items) {
    if (other.id === id && id !== null) continue;
    if (c < other.c + other.w && other.c < c + w && r < other.r + other.h && other.r < r + h) return false;
  }
  return true;
}

/** `findFree`: first free spot for a `w × h` rectangle, scanning rows then columns. */
export function findFree(items: readonly Widget[], w: number, h: number): Cell | null {
  for (let r = 0; r + h <= ROWS; r += 1) for (let c = 0; c + w <= COLS; c += 1) if (fits(items, null, c, r, w, h)) return { c, r };
  return null;
}

/** `place`: where a new widget goes; at `cell` exactly if a size fits there, else the first free spot. */
export function place(items: readonly Widget[], item: WidgetSpec, cell?: Cell | null): Rect | null {
  const s = defSize(item);
  const tries: readonly (readonly [number, number])[] = item.t === 'song' ? [[3, 1], [2, 1]] : [s, [Math.min(s[0], 2), Math.min(s[1], 2)], [1, 1]];
  if (cell) for (const [w, h] of tries) {
    const c = Math.max(0, Math.min(COLS - w, cell.c)), r = Math.max(0, Math.min(ROWS - h, cell.r));
    if (fits(items, null, c, r, w, h)) return { c, r, w, h };
  }
  for (const [w, h] of tries) { const free = findFree(items, w, h); if (free) return { c: free.c, r: free.r, w, h }; }
  return null;
}

export const findIt = (items: readonly Widget[], id: string | null): Widget | null => items.find((item) => item.id === id) ?? null;

export interface Box { readonly x: number; readonly y: number; readonly w: number; readonly h: number; readonly ox: number; readonly oy: number }
/** `boxOf`: the media box inside a `W × H` widget (cover-fitted, zoomed, framed) and its overflow. */
export function boxOf(item: Pick<Widget, 'ar' | 'kind'>, W: number, H: number, px: number, py: number, z: number): Box {
  const a = arOf(item);
  let iw: number, ih: number;
  if (W / H > a) { iw = W; ih = W / a; } else { ih = H; iw = H * a; }
  iw *= z; ih *= z;
  const ox = iw - W, oy = ih - H;
  return { x: -ox * px, y: -oy * py, w: iw, h: ih, ox, oy };
}

export type Corner = 'nw' | 'ne' | 'sw' | 'se';
export interface ResizeStart { readonly corner: Corner; readonly t: WidgetType; readonly c0: number; readonly r0: number; readonly w0: number; readonly h0: number }
/** `resizeRect`: the snapped rectangle while dragging a corner by (`dx`, `dy`) stage pixels. */
export function resizeRect(d: ResizeStart, dx: number, dy: number): Rect {
  const gx = Math.round(dx / STEP), gy = Math.round(dy / STEP), mw = minW(d.t);
  let L = d.c0, T = d.r0, R = d.c0 + d.w0, B = d.r0 + d.h0;
  if (d.corner.includes('w')) L = Math.max(0, Math.min(R - mw, d.c0 + gx));
  if (d.corner.includes('e')) R = Math.max(L + mw, Math.min(COLS, d.c0 + d.w0 + gx));
  if (d.corner.includes('n')) T = Math.max(0, Math.min(B - 1, d.r0 + gy));
  if (d.corner.includes('s')) B = Math.max(T + 1, Math.min(ROWS, d.r0 + d.h0 + gy));
  if (d.t === 'song' && B - T > 2) { if (d.corner.includes('n')) T = B - 2; else B = T + 2; }
  return { c: L, r: T, w: R - L, h: B - T };
}

/** `stepW`: where an arrow key moves a widget, jumping over widgets in the way; null when it cannot move. */
export function stepTarget(items: readonly Widget[], item: Widget, dc: number, dr: number): Cell | null {
  for (let k = 1; k < 7; k += 1) {
    const c = item.c + dc * k, r = item.r + dr * k;
    if (c < 0 || r < 0 || c + item.w > COLS || r + item.h > ROWS) return null;
    if (fits(items, item.id, c, r, item.w, item.h)) return { c, r };
  }
  return null;
}

/** `resizeBy`: the size after Shift + arrow, or null when it does not fit. */
export function resizeTarget(items: readonly Widget[], item: Widget, dw: number, dh: number): { readonly w: number; readonly h: number } | null {
  const w = Math.max(minW(item.t), item.w + dw);
  let h = Math.max(1, item.h + dh);
  if (item.t === 'song') h = Math.min(2, h);
  return fits(items, item.id, item.c, item.r, w, h) ? { w, h } : null;
}

/** `zoomW`: the zoom after multiplying by `factor`, rounded to 3 decimals and clamped. */
export function zoomed(z: number | undefined, factor: number): number {
  const [min, max] = zRange();
  return Math.max(min, Math.min(max, Math.round((z || 1) * factor * 1000) / 1000));
}

/** `panBy`: framing after a keyboard nudge (photos and videos move with the arrow). */
export function panned(item: Pick<Widget, 'px' | 'py'>, dx: number, dy: number): { readonly px: number; readonly py: number } {
  return { px: Math.max(0, Math.min(1, (item.px ?? 0.5) - dx)), py: Math.max(0, Math.min(1, (item.py ?? 0.5) - dy)) };
}

/** A legacy entry: text paragraphs plus media, with no stored layout (06 §6.3). */
export interface LegacyEntry {
  readonly text: readonly string[];
  readonly media: readonly { readonly kind: MediaKind; readonly len?: number; readonly url?: string | null; readonly name?: string; readonly ar?: number }[];
}
/** `autoLayout`: widgets for a legacy entry. `seed` picks one of two arrangements. Only the first media item is used. */
export function autoLayout(entry: LegacyEntry, seed: number): (Omit<Widget, 'id'>)[] {
  const t = entry.text.join('\n\n'), md = entry.media[0], v = seed % 2, out: Omit<Widget, 'id'>[] = [];
  if (!md) { out.push(wd('text', 1, v ? 1 : 2, 5, v ? 3 : 2, { text: t })); return out; }
  const vid = md.kind === 'video';
  const x: Partial<Widget> = { kind: vid ? 'video' : md.kind, len: md.len || 10, url: md.url || null, name: md.name || '' };
  if (md.ar) x.ar = md.ar;
  if (md.kind === 'portrait') { out.push(wd('photo', v ? 4 : 0, 0, 3, 4, x)); if (t) out.push(wd('text', v ? 0 : 3, 1, 4, 3, { text: t })); }
  else if (v) { out.push(wd(vid ? 'video' : 'photo', 0, 0, 7, 4, x)); if (t) out.push(wd('text', 0, 4, 6, 2, { text: t })); }
  else { out.push(wd(vid ? 'video' : 'photo', 1, 0, 5, 3, x)); if (t) out.push(wd('text', 1, 3, 5, 2, { text: t })); }
  return out;
}

/** Pointer position inside the grid box to a cell (`cellAt`). `scale` = rendered width / layout width. */
export function cellAtPoint(x: number, y: number, scale: number): Cell {
  const s = scale || 1;
  return { c: Math.max(0, Math.min(COLS - 1, Math.floor(x / s / STEP))), r: Math.max(0, Math.min(ROWS - 1, Math.floor(y / s / STEP))) };
}
